import { FileSpreadsheet, Loader, Plus, Search } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import DataTablePagination from "../DataTablePagination";
import Filter from "../Filter";
import Spacing from "../Spacing";
import { useLocation } from "react-router-dom";
import { can_visit, debounce, getPractice, notify, numberWithCommas, userMessageTemplates } from "../../helpers";
import { useDocumentTitle } from "../../hooks/customDocumentTitleHook";
import { deleteBulkMipsData, deleteMipsNote, fetchMipsPatients } from "../../services/mips";
import MIPSDataTable from "../DataTable/MIPSDataTable";
import MipsImportModal from "../Modal/MipsImportModal";
import BulkDeleteModal from "../Modal/BulkDeleteModal";

export default function MIPS({selectedPracticeChange, fetchChangePracticeData, smsLimit, setSmsLimit}) {
  let location_id = getPractice("location_id");
  let account_id = getPractice("account_id");
  if (selectedPracticeChange != null && fetchChangePracticeData) {
    location_id = selectedPracticeChange?.location_id;
    account_id = selectedPracticeChange?.account_id;
  }

  const location = useLocation();
  const [mipsPatients, setMipsPatients] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [messageTemplate] = useState(userMessageTemplates());
  const [dataLoading, setDataLoading] = useState(true);
  const [selectedMips, setSelectedMips] = useState([]);
  const [globalCheckedIds, setGlobalCheckedIds] = useState(new Set());
  const [selectedPatientData, setSelectedPatientData] = useState([]);
  const [previousScrollPosition, setPreviousScrollPosition] = useState(0);
  const [mipsImport, setMipsImport] = useState(false);
  const [importLoader, setImportLoader] = useState(false);
  const [allMips] = useState({
    "f_depression": "Depression",
    "f_high_bp_control": "High BP & Controlling High BP",
    "f_current_meds": "Current Medications",
    "f_glycemic_status": "Glycemic Status",
    "f_cervical": "Cervical",
    "f_dm_eye_exam": "DM Eye Exam",
    "f_tobacco": "Tobacco",
    "f_falls_risk": "Falls Risk",
    "f_dementia": "Dementia"
  });

  const parseMips = () => {
    try {
      return (
        JSON.parse(
          new URLSearchParams(location.search).get("mips")
        ) || []
      );
    } catch (error) {
      return [];
    }
  };

  const defaultFilter = {
    account_id: account_id,
    location_id: location_id,
    page: 1,
    per_page: 10,
    search_term: "",
    name: "",
    dob: "",
    sex: "",
    mobile_phone: "",
    start_age: "",
    end_age: "",
    mips: parseMips(),
    order_by: []
  };
  const [filter, setFilter] = useState(defaultFilter);

  const [mipsPatientsCount, setMipsPatientsCount] = useState({
    totalCount: 0,
    totalPages: 0,
  });

  const [checkedIds, setCheckedIds] = useState([]);
  const [checkSelected, setCheckSelected] = useState(checkedIds.length > 0);
  const [isFilterCleared, setIsFilterCleared] = useState(false);
  const [patientFormData, setPatientFormData] = useState(defaultFilter);
  const [mipsBulkDelete, setMipsBulkDelete] = useState(false);



  const handleResetFilterStatus = () => { setIsFilterCleared(false); };

  const handleCheckboxChange = (id, rowData) => {
    const isChecked = checkedIds.includes(id);
    if (isChecked) {
      setCheckedIds(checkedIds.filter((checkedId) => checkedId !== id));
    } else {
      setCheckedIds([...checkedIds, id]);
    }
    setMipsPatients((prevData) =>
      prevData.map((row) =>
        row.id === id ? { ...row, checked: !row.checked } : row
      )
    );
    setGlobalCheckedIds((prevCheckedIds) => {
      const updatedCheckedIds = new Set(prevCheckedIds);
      if (updatedCheckedIds.has(id)) {
        updatedCheckedIds.delete(id);
        setSelectedPatientData((prevData) => prevData.filter((data) => data.id !== id));
      } else {
        updatedCheckedIds.add(id);
        setSelectedPatientData((prevData) => [...prevData, rowData]);
      }
      return updatedCheckedIds;
    });
  };

  const handleSelectAll = (currentPageData) => {
    if (selectAll) {
      const newCheckedIds = checkedIds.filter(
        (id) => !currentPageData.some((row) => row.id === id)
      );

      setCheckedIds(newCheckedIds);
      setGlobalCheckedIds((prevCheckedIds) => {
        const updatedCheckedIds = new Set(prevCheckedIds);
        currentPageData.forEach((row) => updatedCheckedIds.delete(row.id));
        return updatedCheckedIds;
      });
  
      setSelectedPatientData((prevData) =>
        prevData.filter((data) => !currentPageData.some((row) => row.id === data.id))
      );
    } else {
      const newCheckedIds = Array.from(
        new Set([...checkedIds, ...currentPageData.map((row) => row.id)])
      );

      setCheckedIds(newCheckedIds);
      setGlobalCheckedIds((prevCheckedIds) => {
        const updatedCheckedIds = new Set(prevCheckedIds);
        currentPageData.forEach((row) => updatedCheckedIds.add(row.id));
        return updatedCheckedIds;
      });
  
      setSelectedPatientData((prevData) => [
        ...prevData,
        ...currentPageData.filter(
          (row) => !prevData.some((data) => data.id === row.id)
        ),
      ]);
    }

    setSelectAll(!selectAll);

    setMipsPatients((prevData) =>
      prevData.map((row) => ({
        ...row,
        checked: !selectAll,
      }))
    );
  };

  useEffect(() => {
    loadData(filter);
  }, [filter]);

  const loadData = async (getFilter) => {
    setFilter(getFilter);
    let isCanceled = false;

    try {
      const response = await fetchMipsPatients(getFilter);
      const generateData = response.data.results;
      setMipsPatients(generateData);
      setMipsPatientsCount({
        totalCount: response.data.count,
        totalPages: response.data.total_pages,
      });
    } catch (error) {
      if (error?.code === "ERR_CANCELED" || error?.name === "CanceledError") {
        isCanceled = true;
      } else {
        // console.error("Failed to load MIPS patients data", error);
      }
    } finally {
      if (!isCanceled) {
        setDataLoading(false);
      }
    }
  };

  const handlePageChange = (newPage) => {
    setFilter({ ...filter, page: newPage });
    setDataLoading(true);
  };

  const debouncedSetFilter = useCallback(
    debounce((newFilter) => setFilter(newFilter), 300), []
  );

  const handleChangeSearchTerm = (event) => {
    const newFilter = { ...filter, search_term: event.target.value, page: 1 };
    setDataLoading(true);
    debouncedSetFilter(newFilter);
  };

  const handleChangeRowsPerPage = (value) => {
    setFilter({
      ...filter,
      page: 1,
      per_page: value,
    });
    setDataLoading(true);
  };

  useEffect(() => {
    setCheckSelected(checkedIds.length > 0 ? true : false);
  }, [checkedIds]);

  useEffect(() => {
    const sameLocation = filter.location_id === location_id;
    const sameAccount = filter.account_id === account_id;

    if (sameLocation && sameAccount) return;

    setFilter({
      ...filter,
      location_id: location_id,
      account_id: account_id,
    });
    setCheckedIds([]);
    setGlobalCheckedIds(new Set());
    setDataLoading(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location_id, account_id]);

  const handleDeleteRecord = async (id) => {
    setDataLoading(true);
    try {
      const response = await deleteMipsNote(id, account_id, location_id);

      if (response) {
        if (response?.detail) {
          notify('error', response?.detail);
        } else { 
          if(response?.data?.status === 'success'){
            notify("success", response?.data?.message);
            await loadData(filter);
          } else {
            notify("error", response?.data?.message);
          }
        }
      }
    } catch (error) {
      if (error?.response && error?.response?.data && error?.response?.data?.detail) {
        notify("error", error?.response?.data?.detail, 0.5);
      }
    } finally {
      setDataLoading(false);
    }
  };

  const handleBulkDeleteRecord = async (e) => {
    setDataLoading(true);
    try {
      const deleteData = {
        account_id: account_id,
        location_id: location_id,
        selected_id: checkedIds,
      };

      const response = await deleteBulkMipsData(deleteData);
      if (response) {
        if (response?.detail) {
          notify("error", response?.detail);
        } else {
          if(response?.data?.success){
            notify("success", response?.data?.success, 0.5);
            await loadData(filter);
          } else {
            notify("error", response?.data?.error);
          }
        }
      }
    } catch (error) {
      // console.log("🚀 ~ handleBulkDeleteRecord ~ error:", error);
    } finally {
      setDataLoading(false);
    }
  };

  useDocumentTitle(`MIPS`);

  return (
    <>
      <div className="docpro_page_heading_wrap">
        <h2 className="docpro_page_heading docpro_fs_24">
          <i className="docpro_cyan_bg docpro_white_color">
            <FileSpreadsheet width={20} height={20} />
          </i>
          MIPS
          <span>{numberWithCommas(mipsPatientsCount?.totalCount)}</span>
        </h2>
      </div>
      <Spacing lg="12" sm="12" />
      <div className="docpro_data_table_wrap">
        <div className="docpro_data_table_headings small_heading">
          <div className="searching_div">
            <form className="docpro_search_form width_100">
              <input
                type="search"
                placeholder="Search"
                className="docpro_search_input"
                onChange={handleChangeSearchTerm}
              />
              <button className="docpro_search_btn">
                <Search width={20} height={20} />
              </button>
            </form>
          </div>

          <div className='docpro_data_table_btns p-0 small_filter_btns3'>
            {(can_visit('mips') && can_visit('mips_import')) && (
              <button 
                className="docpro_bordered_btn docpro_fw_600 docpro_primary_color small_device2"
                onClick={() => {
                  setMipsImport(!mipsImport);
                  setPreviousScrollPosition(window.scrollY);
                  window.scrollTo(0, 0);
                }}
              >
                <Plus width={20} height={20} />
                <span>Import</span>
                {importLoader && (
                  <span className="docpro_loading">
                    <Loader width={20} height={20} />
                  </span>
                )}
              </button>
            )}

            <Filter
              filterType="patient_mips"
              reloadData={loadData}
              reloadDataFilter={filter}
              setDataLoading={setDataLoading}
              parseMips={parseMips}
              isFilterCleared={isFilterCleared}
              onResetFilterStatus={handleResetFilterStatus}
              patientFormData={patientFormData}
              setPatientFormData={setPatientFormData}
              selectedMips={selectedMips || []}
              setSelectedMips={setSelectedMips}
              setShowSave={false}
              allMips={allMips}
            />
          </div>
        </div>

        <MIPSDataTable
          tableData={mipsPatients}
          handleSelectAll={() => handleSelectAll(mipsPatients)}
          handleCheckboxChange={handleCheckboxChange}
          checkedIds={checkedIds}
          setCheckedIds={setCheckedIds}
          checkSelected={checkSelected}
          reloadData={loadData}
          reloadDataFilter={filter}
          messageTemplate={messageTemplate}
          account_id={account_id}
          location_id={location_id}
          smsLimit={smsLimit}
          setSmsLimit={setSmsLimit}
          dataLoading={dataLoading}
          setDataLoading={setDataLoading}
          globalCheckedIds={globalCheckedIds}
          setGlobalCheckedIds={setGlobalCheckedIds}
          selectedPatientData={selectedPatientData}
          setSelectedPatientData={setSelectedPatientData}
          previousScrollPosition={previousScrollPosition}
          setPreviousScrollPosition={setPreviousScrollPosition}
          mipsBulkDelete={mipsBulkDelete}
          setMipsBulkDelete={setMipsBulkDelete}
          handleDeleteRecord={handleDeleteRecord}
        />
        {mipsPatients.length > 0 && !dataLoading && (
          <DataTablePagination
            currentPage={filter.page}
            totalPages={mipsPatientsCount.totalPages}
            onPageChange={handlePageChange}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            totalRecords={mipsPatientsCount.totalCount}
            currentPageData={filter.per_page}
          />
        )}
      </div>

      {mipsImport && (
        <MipsImportModal
          account_id={account_id}
          location_id={location_id}
          reloadData={loadData}
          reloadDataFilter={filter}
          mipsImport={mipsImport}
          setMipsImport={setMipsImport}
          previousScrollPosition={previousScrollPosition}
          setImportLoader={setImportLoader}
        />
      )}

      {mipsBulkDelete && (
        <BulkDeleteModal
          handleDelete={() => handleBulkDeleteRecord()}
          name={`${checkedIds?.length} records`}
          activeModal={mipsBulkDelete}
          setActiveModal={setMipsBulkDelete}
          previousScrollPosition={previousScrollPosition}
        />
      )}
    </>
  );
}
